<script lang="ts" setup>
import type { ApiCommentGetAll, CreateComment } from '~/types'
import type { ApiCustomerModuleCommentGetAll } from '~/layers/customer-module/types'
import type { CommentReactionType } from '~/prisma/enums'

const props = defineProps<{
  comment: ApiCommentGetAll | ApiCustomerModuleCommentGetAll
  isReadOnly: boolean
  isDeleteDisabled: boolean
  isVisibilityAdjustable: boolean
  entityId: string
  showReplyForm?: boolean
}>()
const emit = defineEmits<{
  (e: 'create', payload: CreateComment['data']): void
  (e: 'update', commentId: string): void
  (e: 'delete', commentId: string): void
  (e: 'reactionUpsert', commentId: string, type: CommentReactionType): void
  (e: 'reactionDelete', commentId: string): void
  (e: 'reply', { isOpen, comment }: { isOpen: boolean, comment?: ApiCommentGetAll }): void
}>()
const expandedNames = ref<string[]>(['replies'])

// Open replies when new replies are added
watch(() => props.comment.totalRepliesCount, (curr, prev) => {
  if (curr > prev) {
    expandedNames.value = ['replies']
  }
})

// Open replies when new reply form needs to be shown
watch (() => props.showReplyForm, (isShown) => {
  if (isShown) {
    expandedNames.value = ['replies']
  }
})

// Watch when the replies are closed and then remove the reply field
watch(expandedNames, (names) => {
  if (!names.includes('replies')) {
    emit('reply', { isOpen: false })
  }
})

const lastAction = computed(() => props.comment.actions[0])
</script>

<template>
  <n-collapse v-if="comment.totalRepliesCount > 0" v-model:expanded-names="expandedNames">
    <div class="border border-t-0 p-3 rounded rounded-t-none">
      <n-collapse-item name="replies" :title="$t('comment.replies.showReplies', { count: comment.totalRepliesCount })" class="repliesHeader">
        <template v-if="lastAction" #header-extra>
          <span class="text-xs opacity-75">
            <template v-if="lastAction.type === 'reaction'">
              {{ $t('comment.lastActivity.reaction', { name: lastAction.user?.name ?? lastAction.user?.email, at: useDateAsString(lastAction.createdAt) }) }}
            </template>
            <template v-else-if="lastAction.type === 'message'">
              {{ $t('comment.lastActivity.message', { name: lastAction.user?.name ?? lastAction.user?.email, at: useDateAsString(lastAction.createdAt) }) }}
            </template>
          </span>
        </template>

        <div class="space-y-2">
          <CommentItem
            v-for="reply of comment.replies"
            v-bind="props"
            :key="reply.id"
            :comment="reply"
            :is-reply-disabled="showReplyForm"
            @create="$emit('create', $event)"
            @update="$emit('update', $event)"
            @delete="$emit('delete', $event)"
            @reaction-upsert="(id, type) => $emit('reactionUpsert', id, type)"
            @reaction-delete="$emit('reactionDelete', $event)"
            @reply="$emit('reply', $event)"
          />

          <slot />
        </div>
      </n-collapse-item>
    </div>
  </n-collapse>
  <div v-else-if="showReplyForm">
    <div class="border border-t-0 p-2 rounded rounded-t-none">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.repliesHeader :deep(.n-collapse-item__header-main) {
  white-space: nowrap;
}
.repliesHeader :deep(.n-collapse-item__header-extra) {
  margin-left: 0.5rem;
}
</style>
